import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useDropzone } from "react-dropzone";
import {
  Upload,
  Database,
  FileText,
  Trash2,
  Eye,
  MoreVertical,
  Plus,
} from "lucide-react";
import { dataSourceAPI } from "../services/api";
import toast from "react-hot-toast";

const DataSources: React.FC = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const queryClient = useQueryClient();

  const {
    data: dataSources = [],
    isLoading,
    error,
  } = useQuery("dataSources", dataSourceAPI.getDataSources);

  const deleteDataSourceMutation = useMutation(dataSourceAPI.deleteDataSource, {
    onSuccess: () => {
      queryClient.invalidateQueries("dataSources");
      toast.success("Data source deleted successfully");
    },
    onError: () => {
      toast.error("Failed to delete data source");
    },
  });

  const handleDelete = (id: string) => {
    if (window.confirm("Are you sure you want to delete this data source?")) {
      deleteDataSourceMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Failed to load data sources</div>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Data Sources</h1>
          <p className="text-gray-600 mt-1">
            Manage your data sources and upload new datasets
          </p>
        </div>

        <button
          onClick={() => setShowUploadModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="h-5 w-5" />
          <span>Add Data Source</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-lg">
              <Database className="h-6 w-6 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sources</p>
              <p className="text-2xl font-bold text-gray-900">
                {dataSources.length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <FileText className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">CSV Files</p>
              <p className="text-2xl font-bold text-gray-900">
                {dataSources.filter((ds: any) => ds.type === "csv").length}
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Rows</p>
              <p className="text-2xl font-bold text-gray-900">
                {dataSources.reduce(
                  (sum: number, ds: any) => sum + (ds.row_count || 0),
                  0
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Sources List */}
      {dataSources.length === 0 ? (
        <div className="text-center py-12">
          <Database className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No data sources yet
          </h3>
          <p className="text-gray-600 mb-6">
            Upload your first CSV file to get started
          </p>
          <button
            onClick={() => setShowUploadModal(true)}
            className="btn-primary"
          >
            Upload CSV
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dataSources.map((dataSource: any) => (
            <DataSourceCard
              key={dataSource.id}
              dataSource={dataSource}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            queryClient.invalidateQueries("dataSources");
          }}
        />
      )}
    </div>
  );
};

interface DataSourceCardProps {
  dataSource: any;
  onDelete: (id: string) => void;
}

const DataSourceCard: React.FC<DataSourceCardProps> = ({
  dataSource,
  onDelete,
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "csv":
        return <FileText className="h-5 w-5" />;
      case "database":
        return <Database className="h-5 w-5" />;
      default:
        return <Database className="h-5 w-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "csv":
        return "text-green-600 bg-green-100";
      case "database":
        return "text-blue-600 bg-blue-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  return (
    <div className="card p-6 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {dataSource.name}
          </h3>
          <div className="flex items-center space-x-2 mb-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(
                dataSource.type
              )}`}
            >
              {getTypeIcon(dataSource.type)}
              <span className="ml-1 capitalize">{dataSource.type}</span>
            </span>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                dataSource.status === "active"
                  ? "text-green-800 bg-green-100"
                  : "text-red-800 bg-red-100"
              }`}
            >
              {dataSource.status}
            </span>
          </div>
          <p className="text-sm text-gray-600">
            {dataSource.row_count?.toLocaleString() || 0} rows
          </p>
        </div>

        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <MoreVertical className="h-5 w-5" />
          </button>

          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-10">
              <div className="py-1">
                <button className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  <Eye className="h-4 w-4 mr-3" />
                  View Data
                </button>
                <button
                  onClick={() => {
                    onDelete(dataSource.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4 mr-3" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="text-sm text-gray-500">
        <p>Created {formatDate(dataSource.created_at)}</p>
        {dataSource.schema?.columns && (
          <p className="mt-1">{dataSource.schema.columns.length} columns</p>
        )}
      </div>

      {/* Schema Preview */}
      {dataSource.schema?.columns && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-xs font-medium text-gray-700 mb-2">Columns:</p>
          <div className="flex flex-wrap gap-1">
            {dataSource.schema.columns
              .slice(0, 5)
              .map((column: any, index: number) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700"
                >
                  {column.name}
                </span>
              ))}
            {dataSource.schema.columns.length > 5 && (
              <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
                +{dataSource.schema.columns.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
interface UploadModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ onClose, onSuccess }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    name: "",
  });

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    if (!formData.name.trim()) {
      setFormData({ name: file.name.replace(".csv", "") });
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await dataSourceAPI.uploadCSV(
        file,
        formData.name || file.name.replace(".csv", "")
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => {
        onSuccess();
        toast.success("CSV uploaded successfully!");
      }, 500);
    } catch (error: any) {
      toast.error(error.response?.data?.error || "Failed to upload CSV");
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Upload CSV File
          </h3>

          {!isUploading ? (
            <div className="space-y-4">
              <div>
                <label className="label">Data Source Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ name: e.target.value })}
                  className="input"
                  placeholder="Enter a name for this data source"
                />
              </div>

              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? "border-primary-400 bg-primary-50"
                    : "border-gray-300 hover:border-gray-400"
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                {isDragActive ? (
                  <p className="text-primary-600">Drop the CSV file here...</p>
                ) : (
                  <div>
                    <p className="text-gray-600 mb-2">
                      Drag and drop a CSV file here, or click to select
                    </p>
                    <p className="text-sm text-gray-500">
                      Only CSV files are supported
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <Upload className="h-12 w-12 text-primary-600 mx-auto mb-4 animate-pulse" />
                <p className="text-gray-600 mb-4">
                  Uploading and processing CSV...
                </p>

                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {uploadProgress}% complete
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataSources;




